import { ensureDefined, SMap } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { HpError } from '@avantia/logging-and-errors';
import {
  createControlId,
  HpAnswerMap,
  HpAnswerValueTypes,
  HpQuestionSet,
  setCustomerAnswer
} from '@avantia/questionset-model';
import { v4 as uuid } from 'uuid';
import { HpBankDetailsValidationFailedPayload } from '../actions/validateBankDetailsAction';
import { HpModalQuestionUI } from '../components/formControls/custom';
import { HpClaimsQuestionSetPageStateProps } from '../components/questionSetForm/questionSetPageTypes';
import { getConfig } from '../config/configuration';
import { HpDynamicButtonClickAction } from '../engineClient/actions/dynamicButtonClickActionTypes';
import { triggerFieldChangedEvent } from '../engineClient/actions/eventActions';
import { HpValidateAndMaybeMoveSectionProps } from '../engineClient/customExtensionRegistryTypes';
import { questionSetReducer, questionSetReducerProcessor } from '../engineClient/reducers/questionSetReducer';
import {
  addControlGroup,
  moveSectionWithoutChecks,
  processPrereqsAndCalcs,
  validateAndMaybeMoveSection
} from '../engineClient/reducers/questionSetReducerLibrary';
import { validateSection } from '../engineClient/reducers/questionSetTools';
import { applyChanges } from '../engineClient/reducers/reducerLibrary';
import { getAnswersFromAnswerMap } from '../engineClient/tools/clientAndServerUtilities';
import {
  HpClientAnswer,
  HpClientQuestion,
  HpClientQuestionData,
  HpClientQuestionSetState,
  HpClientQuestionUI,
  HpClientValidationResult,
  HpEngineActionTypes,
  HpEventTypes,
  HpFieldChangedAction,
  HpQuestionIdNode
} from '../engineClient/types';
import { applyPolicyAnswers } from '../tools/policyAnswers';
import {
  coverTypeMap,
  filterPolicyInstances,
  getCopyTextFromCustomData,
  getCustomDataSection,
  getPolicyMapFromAnswers,
  HpClaimsCommonCustomData
} from '../tools/utilities';
import {
  HpAlertConditionsMap,
  HpClaimAlertConditions,
  HpClaimsAlertCustomData,
  HpCoverAlertPageDetails
} from '../types/alertTypes';
import { notListedSpecifiedItem, Policy, PolicyMap, SpecifiedItem } from '../types/policyTypes';
import { questionIds, sectionIds } from '../types/questionConstants';
import { recalculateScoringMap } from '../tools/scoringUtilities';

const { developerModeEnabled, isClaimsRoutingEnabled, productIdDefault: productId } = getConfig();

// TODO AL - Create HpClaimsQuestionSetState from HpClaimsQuestionSetPageStateProps
questionSetReducerProcessor.add<HpFieldChangedAction>(
  'FIELD_CHANGED' as HpEngineActionTypes,
  (state, { payload }) => {
    if (!payload) {
      return state;
    }

    const { value, question } = payload;
    const event = payload.getEvent();
    const eventType = event.type as HpEventTypes;
    let newState: HpClientQuestionSetState = state;
    const { questionId } = question;
    const {
      policyInstance: policyQid,
      lossDate,
      specifiedItems: specifiedItemsQuestionId,
      adCoverLevel: adCoverLevelQid,
      coverType: coverTypeQid,
      specifiedItemCoverLevel,
      isCoverAlertTriggered: isCoverAlertTriggeredQid,
      specifiedItemsOther: specifiedItemsOtherQid
    } = questionIds;

    const { policies, answers, questions, commonCustomData, displayState } = state as HpClaimsQuestionSetPageStateProps;
    ensureDefined(commonCustomData, 'commonCustomData');
    let selectedPolicyInstance: string | null = null;
    let newAnswers = lodash.cloneDeep(answers);
    newState = applyChanges(newState, { answers: newAnswers });

    let hasChanges = false;
    if (questionId === lossDate) {
      ({
        answers: newAnswers,
        selectedPolicyInstance,
        state: newState
      } = maybeAutoSelectPolicy({
        questions,
        answers: newAnswers,
        selectedDate: value,
        policies,
        policyQid,
        state: newState
      }));

      if (!selectedPolicyInstance) {
        return newState;
      }

      hasChanges = true;
    } else if (questionId === policyQid) {
      selectedPolicyInstance = value as string;
      const selectedPolicy = policies[selectedPolicyInstance];
      newState = initialisePolicyAnswers({
        policy: selectedPolicy,
        questions,
        state: newState
      });
      newAnswers = newState.answers;
      hasChanges = true;
    } else if (questionId === coverTypeQid) {
      const { coverType, policyInstance } = getAnswersFromAnswerMap({
        answers,
        keys: ['coverType', 'policyInstance'],
        options: { returnAnyAnswerType: true },
        productId: state.productId
      });

      // B&C has same copy for both Ad cover levels.
      //TODO: Remove B&C check if the below needs to be executed if B&C selected.
      if (coverType === coverTypeMap.BuildingsAndContents || !policyInstance) {
        return state;
      }

      const selectedPolicy = policies[policyInstance];
      const { accidentalDamageLevel } = selectedPolicy;
      let adCoverLevelAnswer: 'BASIC' | 'FULL' | 'NONE';
      const accidentalDamageLevelSelected = accidentalDamageLevel && accidentalDamageLevel[coverType as string];
      if (accidentalDamageLevelSelected) {
        adCoverLevelAnswer = accidentalDamageLevelSelected.toUpperCase();
      } else {
        adCoverLevelAnswer = 'NONE';
      }

      setCustomerAnswer(newAnswers, adCoverLevelQid, adCoverLevelAnswer);
      hasChanges = true;
    } else if (questionId === specifiedItemsQuestionId) {
      selectedPolicyInstance = answers[policyQid].customer as string;
      if (!selectedPolicyInstance) {
        return state;
      }

      let siCoverLevel: 'HOME' | 'AWAY' | 'NONE' | undefined = undefined;
      if (value !== notListedSpecifiedItem) {
        const selectedPolicy = policies[selectedPolicyInstance];
        const { specifiedItems } = selectedPolicy;
        const selectedItem = specifiedItems.find((item) => item.description === value) as SpecifiedItem;
        const { isAwayFromHomeCover } = selectedItem;
        siCoverLevel = isAwayFromHomeCover ? 'AWAY' : 'HOME';
      }

      setCustomerAnswer(newAnswers, specifiedItemCoverLevel, siCoverLevel || 'NONE');
      setCustomerAnswer(newAnswers, specifiedItemsOtherQid, null);
      setAffectedSpecifiedItemsAnswers(policies, newAnswers);
      hasChanges = true;
    } else if (questionId === specifiedItemsOtherQid) {
      setAffectedSpecifiedItemsAnswers(policies, newAnswers);
      hasChanges = true;
    }

    const { haveAlertsBeenUpdated } = processAlertConditions({
      questions,
      answers: newAnswers,
      commonCustomData: commonCustomData as unknown as HpClaimsCommonCustomData,
      isCoverAlertTriggeredQid
    });

    let hasScoreChanged = false;
    if (isClaimsRoutingEnabled) {
      const scoreResult = applyScoreUpdate(newState as HpClaimsQuestionSetPageStateProps, newAnswers);
      newState = scoreResult.state;
      hasScoreChanged = scoreResult.hasScoreChanged;
    }

    hasChanges = hasChanges || haveAlertsBeenUpdated || hasScoreChanged;

    const { questionsUI } = displayState;
    if (hasChanges) {
      newState = applyChanges(newState, { answers: newAnswers });
      newState = processPrereqsAndCalcs(eventType, newState, { questionsUI });

      const { policyInstance: prevPolicyInstance } = getAnswersFromAnswerMap({
        answers,
        keys: ['policyInstance'],
        options: { returnAnyAnswerType: true },
        productId: state.productId
      });
      if (prevPolicyInstance !== selectedPolicyInstance) {
        const { questionFormats } = newState;
        const ui = questionsUI[policyQid];
        triggerFieldChangedEvent({ question, questionFormats, ui, value, key: policyQid, getEvent: () => event });
      }
    }

    return newState;
  },
  { isDuplicate: true }
);

questionSetReducerProcessor.add<HpDynamicButtonClickAction>(
  'DYNAMIC_BUTTON_CLICK' as HpEngineActionTypes,
  (state) => {
    if (!isClaimsRoutingEnabled) {
      return state;
    }

    const newAnswers = lodash.cloneDeep(state.answers);
    const { state: updatedState, hasScoreChanged } = applyScoreUpdate(
      state as HpClaimsQuestionSetPageStateProps,
      newAnswers
    );
    if (!hasScoreChanged) {
      return state;
    }

    return applyChanges(updatedState, { answers: newAnswers });
  },
  { isDuplicate: true }
);

questionSetReducerProcessor.add<HpDynamicButtonClickAction>(
  'BANK_DETAILS_VALIDATION_FAILED' as HpEngineActionTypes,
  (state, { payload }) => {
    if (!payload) {
      return state;
    }

    const { errorMessage, invalidQuestionId } = payload as unknown as HpBankDetailsValidationFailedPayload;
    let newState = state;
    const { errors, count } = validateSection({ state: newState, sectionId: sectionIds.bankDetails });
    const hasErrors = !!errorMessage;
    const { displayState } = newState;
    const { questionsUI } = displayState;
    displayState.showValidationSummary = hasErrors;
    const validationResult: HpClientValidationResult = {
      ...state.validationResult,
      errors: {
        ...errors,
        [invalidQuestionId]: errorMessage as string
      },
      count: count + 1
    };

    const newDisplayState = applyChanges(displayState, { questionsUI });
    newState = applyChanges(state, { validationResult, displayState: newDisplayState });
    return newState;
  }
);

questionSetReducerProcessor.add<any>('POLICIES_RETRIEVED' as HpEngineActionTypes, (state, action) => {
  const { payload } = action;

  // The phase and the section a resumed claim opens on are the server's: only it knows what SiteView says the
  // claim has reached, and it sends them in the answers, which the engine applies as it loads them.
  let newState = addPoliciesToState(state as any, payload);
  if (developerModeEnabled && !newState.answersId) {
    newState = applyChanges(newState, { answersId: uuid() });
  }

  return newState;
});

// We also need to process the policies after retrieving answers. Policies are stored in the answers.
questionSetReducerProcessor.add<any>(
  'ANSWERS_RETRIEVED',
  (state, { payload }) => {
    return addPoliciesToState(state as any, payload);
  },
  { isDuplicate: true }
);

questionSetReducerProcessor.add<any>('MODAL_TOGGLED' as HpEngineActionTypes, (state, action) => {
  const { displayState } = state;
  const { questionsUI } = displayState;
  const { question, toggle } = action.payload;
  const { questionId } = question;
  const modalQuestionUIToUpdate = questionsUI[questionId] as HpModalQuestionUI;
  const modalQuestionUI = (modalQuestionUIToUpdate.isModalVisible = toggle);
  questionsUI[questionId] = applyChanges(questionsUI[questionId], modalQuestionUI as unknown as HpClientQuestionUI);
  const newDisplayState = applyChanges(displayState, { questionsUI });

  return applyChanges(state, { displayState: newDisplayState });
});

function addPoliciesToState(state: HpClaimsQuestionSetPageStateProps, payload: HpQuestionSet | undefined) {
  if (payload) {
    const policyData = getPolicyMapFromAnswers(payload.answers, payload.productId);
    return applyChanges(state, { policies: policyData });
  }

  return state;
}

interface MaybeSelectPolicyByDateProps {
  policies: PolicyMap;
  selectedDate: string;
  answers: HpAnswerMap;
  policyQid: string;
  questions: SMap<HpClientQuestion>;
  state: HpClientQuestionSetState;
}

/**
 * Given a date it will first filter the policies by start date.
 * If there is only policy, it will update the answers and return it.
 * If no policies are found or there is more than one for that date, it will return the original answers.
 */
export function maybeAutoSelectPolicy({
  policies,
  selectedDate,
  answers,
  policyQid,
  questions,
  state
}: MaybeSelectPolicyByDateProps): {
  selectedPolicyInstance: string | null;
  answers: HpAnswerMap;
  state: HpClientQuestionSetState;
} {
  let newState: HpClientQuestionSetState = state;
  const filteredPolicies = filterPolicyInstances(policies, new Date(selectedDate as string));
  const policiesReferences = filteredPolicies && Object.keys(filteredPolicies);
  if (policiesReferences && policiesReferences.length === 1) {
    const selectedPolicyInstance = policiesReferences[0];
    setCustomerAnswer(answers, policyQid, selectedPolicyInstance);
    const selectedPolicy = policies[selectedPolicyInstance];
    newState = initialisePolicyAnswers({
      policy: selectedPolicy,
      questions,
      state
    });
    return {
      selectedPolicyInstance,
      answers,
      state
    };
  }

  return { selectedPolicyInstance: null, answers, state: newState };
}

interface HpClaimsProcessAlertConditions {
  questions: SMap<HpClientQuestion>;
  answers: SMap<HpClientAnswer>;
  commonCustomData: HpClaimsCommonCustomData;
  isCoverAlertTriggeredQid: string;
}

// TODO AL - Add unit tests.
export function processAlertConditions({ answers, questions, commonCustomData }: HpClaimsProcessAlertConditions): {
  haveAlertsBeenUpdated: boolean;
} {
  let haveAlertsBeenUpdated = false;
  const {
    perilType,
    coverType,
    whatWasDamaged,
    whatWasDamagedSomethingElse,
    whereWasDamaged,
    specifiedItemCoverLevel,
    itemWorth,
    adCoverLevel,
    areSpecifiedItemsAffected
  } = getAnswersFromAnswerMap({
    productId,
    answers,
    keys: [
      'perilType',
      'coverType',
      'whatWasDamaged',
      'whatWasDamagedSomethingElse',
      'whereWasDamaged',
      'specifiedItemCoverLevel',
      'itemWorth',
      'adCoverLevel',
      'areSpecifiedItemsAffected'
    ],
    options: { returnAnyAnswerType: true }
  });
  const alertConditionsMap = getCustomDataSection<HpAlertConditionsMap>(
    'alertConditionsMap',
    commonCustomData as unknown as HpClaimsCommonCustomData
  );
  const conditionAnswersMap: HpClaimAlertConditions = {
    perilType,
    coverType,
    whatWasDamaged: whatWasDamagedSomethingElse || whatWasDamaged,
    whereWasDamaged,
    specifiedItemCoverLevel,
    itemWorth,
    adCoverLevel,
    areSpecifiedItemsAffected
  };

  //TODO: Should create a question map which only affects the presence of alerts and only loop through those questions instaead all questions
  for (const questionId in questions) {
    const question = questions[questionId];
    const { name, data } = question;
    if (!name.endsWith('Alert')) {
      continue;
    }

    const { alerts: alertsCopyText } = getCopyTextFromCustomData(commonCustomData);
    const { customData } = data as HpClientQuestionData;
    const { alerts } = customData as unknown as HpClaimsAlertCustomData;
    ensureDefined(alertConditionsMap, 'alertConditionsMap');
    let textThatMetCondition: string | HpCoverAlertPageDetails | null = null;

    for (const alert of alerts) {
      const { condition, text, heading } = alert;
      let isMet = false;
      if (Array.isArray(condition)) {
        for (const code of condition) {
          isMet = showAlert(code, alertConditionsMap, conditionAnswersMap);
          if (isMet) {
            break;
          }
        }
      } else {
        isMet = showAlert(condition, alertConditionsMap, conditionAnswersMap);
      }

      const { isCoverAlertTriggered: isCoverAlertTriggeredQid } = questionIds;

      setCustomerAnswer(answers, isCoverAlertTriggeredQid, isMet);

      if (isMet) {
        const alertText = ensureDefined(alertsCopyText, 'alerts')[text];
        let alertCommonText: string;
        if (heading) {
          alertCommonText = JSON.stringify({
            heading: getHeadingByAlertTextCode(commonCustomData, heading),
            text: alertText
          });
        } else {
          alertCommonText = alertText;
        }

        textThatMetCondition = alertCommonText || text;

        break;
      }
    }

    const answer = answers[questionId] && answers[questionId].customer;
    if (answer !== textThatMetCondition) {
      setCustomerAnswer(answers, questionId, textThatMetCondition);
      haveAlertsBeenUpdated = true;
    }
  }

  return { haveAlertsBeenUpdated };
}

interface ApplyPolicyInstanceChangesProps {
  policy: Policy;
  questions: SMap<HpClientQuestion>;
  state: HpClientQuestionSetState;
}

/**
 * Initialises policy dependent answers, including questionSetId.
 */
/**
 * Writes the answers the policy owns into the state. The rule itself lives in `applyPolicyAnswers`, which the
 * server calls too, so a claim started without a browser derives the same answers.
 */
export function initialisePolicyAnswers({ policy, questions, state }: ApplyPolicyInstanceChangesProps) {
  applyPolicyAnswers({
    policy,
    answers: state.answers,
    questions,
    sections: state.displayState.sections,
    productId
  });

  return state;
}

export function showAlert<ConditionsModelT>(
  code: string,
  alertConditionsMap: SMap<ConditionsModelT>,
  conditionAnswersMap: ConditionsModelT
): boolean {
  const alertConditions = alertConditionsMap[code];
  if (!alertConditions) {
    throw new HpError(`Question alert code: ${code} not found in alertConditionsMap`);
  }

  const isMet = Object.keys(alertConditions).every((key) => {
    const condition = alertConditions[key];
    const claimAnswer = conditionAnswersMap[key];
    if (Array.isArray(condition)) {
      return condition.includes(claimAnswer);
    }

    // TODO - The script that imports the alerts, should check that the condition value matches one of the lookup values, otherwise should throw an error.
    return conditionAnswersMap[key] === condition;
  });

  return isMet;
}

export function getHeadingByAlertTextCode(commonCustomData: HpClaimsCommonCustomData, heading: string) {
  const { alerts: alertsCopyText } = getCopyTextFromCustomData(commonCustomData);
  const alertHeading = ensureDefined(alertsCopyText, 'alerts')[heading];

  return alertHeading;
}

export function validateAndMaybeMoveSectionExtension(
  props: HpValidateAndMaybeMoveSectionProps
): HpClientQuestionSetState {
  const { buttonAction, state, desiredSectionId } = props;
  const { validationResult, displayState } = state;
  const { sections, sectionsUI } = displayState;
  const hasErrors = !!validationResult.count;

  if (hasErrors) {
    return state;
  }

  const { coverAlert } = sectionIds;
  const coverAlertUI = sectionsUI[coverAlert];
  const isCoverAlertVisible = !!coverAlertUI && coverAlertUI.isVisible;

  //return to alert page if cover alert triggered
  if (
    desiredSectionId &&
    sections[desiredSectionId].position > sections[coverAlert].position &&
    isCoverAlertVisible &&
    buttonAction === 'move-to-section'
  ) {
    return moveSectionWithoutChecks({ state: props.state, moveToSectionId: coverAlert });
  } else {
    return state;
  }
}

export function sortQuestionIdNodesUsingListExtension(
  visibleQuestions: HpQuestionIdNode[],
  questionIds: string[]
): HpQuestionIdNode[] {
  const idToIndexMap: SMap<number> = {};
  visibleQuestions.forEach((vq, index) => {
    if (typeof vq === 'string') {
      idToIndexMap[vq] = index;
    } else {
      addIdToMap(index, vq);
    }
  });

  const list: HpQuestionIdNode[] = [];
  const seen: SMap<true> = {}; // we don't want to add a group twice.
  let buttonGroupIndex: number | undefined;
  questionIds.forEach((questionId) => {
    const ix = idToIndexMap[questionId];
    if (ix !== undefined) {
      const node = visibleQuestions[ix];
      if (typeof node === 'string') {
        list.push(node);
      } else if (!seen[questionId]) {
        const { groupId, questions } = node;
        if (groupId === 'button-grouping' && buttonGroupIndex === undefined) {
          buttonGroupIndex = list.length;
        }

        questions.forEach((id) => {
          if (typeof id === 'string') {
            seen[id] = true;
          }
        });
        list.push(node);
      }
    }
  });

  return list;

  function addIdToMap(index: number, vq: string | HpQuestionIdNode): void {
    if (typeof vq === 'string') {
      idToIndexMap[vq] = index;
    } else {
      vq.questions.forEach((idOrGroupNode) => {
        if (typeof idOrGroupNode === 'string') {
          idToIndexMap[idOrGroupNode] = index;
        } else {
          addIdToMap(index, idOrGroupNode);
        }
      });
    }
  }
}

function setAffectedSpecifiedItemsAnswers(policies: PolicyMap, answers: HpAnswerMap): void {
  const affectedSpecifiedItems = getAffectedSpecifiedItems(answers);
  setCustomerAnswer(
    answers,
    questionIds.affectedSpecifiedItems,
    affectedSpecifiedItems as unknown as HpAnswerValueTypes
  );
  setCustomerAnswer(answers, questionIds.areSpecifiedItemsAffectedValue, !!affectedSpecifiedItems.length);

  function getAffectedSpecifiedItems(answers: HpAnswerMap): SpecifiedItem[] {
    const selectedPolicyInstance = answers[questionIds.policyInstance].customer as string;
    const selectedPolicy = policies[selectedPolicyInstance];
    const specifiedItemsDetail = selectedPolicy.specifiedItems;
    const { specifiedItems, specifiedItemsOther } = getAnswersFromAnswerMap({
      productId,
      answers,
      keys: ['specifiedItems', 'specifiedItemsOther'],
      options: { returnAnyAnswerType: true }
    });

    let affectedItems: SpecifiedItem[] = [];
    if (!specifiedItems || (specifiedItems === notListedSpecifiedItem && !specifiedItemsOther)) {
      return affectedItems;
    }

    if (specifiedItemsOther) {
      affectedItems = specifiedItemsDetail.filter((item) => specifiedItemsOther.includes(item.description));
    }

    if (specifiedItems === notListedSpecifiedItem) {
      return affectedItems;
    }

    const affectedSIDetail = specifiedItemsDetail.find((item) =>
      specifiedItems.includes(item.description)
    ) as SpecifiedItem;
    affectedItems.push(affectedSIDetail);
    return affectedItems;
  }
}

// Repeater sections render no instances until an add-control-group runs.
// Seed the first group instance when the peril flow is entered so the user sees an empty entry to fill in.
const repeaterSeeds: { sectionId: string; firstQuestionId: string; addButtonId: string }[] = [
  {
    sectionId: sectionIds.damagedRoom,
    firstQuestionId: questionIds.roomType,
    addButtonId: questionIds.addAnotherRoomButton
  },
  {
    sectionId: sectionIds.damagedItem,
    firstQuestionId: questionIds.itemCategory,
    addButtonId: questionIds.adAddAnotherItemButton
  }
];

function ensureFirstDamagedRoom(state: HpClientQuestionSetState): HpClientQuestionSetState {
  for (const { sectionId, firstQuestionId, addButtonId } of repeaterSeeds) {
    state = seedRepeaterGroup(state, sectionId, firstQuestionId, addButtonId);
  }

  return state;
}

function seedRepeaterGroup(
  state: HpClientQuestionSetState,
  groupSectionId: string,
  firstQuestionId: string,
  addButtonQuestionId: string
): HpClientQuestionSetState {
  const section = state.displayState.sections[groupSectionId];
  if (!section || section.isRepeater !== true) {
    return state;
  }

  const firstControlId = createControlId({
    questionId: firstQuestionId,
    groupNumber: 1,
    options: { noPrefix: true }
  });
  if (state.questions[firstControlId]) {
    return state;
  }

  return addControlGroup({
    groupId: groupSectionId,
    sourceQuestionId: addButtonQuestionId,
    state
  });
}

// Advances to the next visible section via the same path as the Continue button (next-section),
// bypassing the highestSectionStartedPosition clamp that applies to move-to-section jumps.
// Dispatched by submitClaimAction on EoW submit success. Also seeds the first damaged-room instance
// so the repeating rooms section shows an empty room on entry.
questionSetReducerProcessor.add<any>('CLAIM_ADVANCE_TO_NEXT_SECTION' as HpEngineActionTypes, (state, action) => {
  let workingState = state;
  const payload = action.payload as { answers?: HpAnswerMap } | undefined;
  if (payload && payload.answers) {
    const mergedAnswers = { ...state.answers, ...payload.answers };
    workingState = processPrereqsAndCalcs('none', applyChanges(state, { answers: mergedAnswers }));
  }

  let newState = validateAndMaybeMoveSection({ state: workingState, buttonAction: 'next-section' });
  newState = ensureFirstDamagedRoom(newState);
  return processPrereqsAndCalcs('none', newState);
});

interface ApplyScoreUpdateResult {
  state: HpClaimsQuestionSetPageStateProps;
  hasScoreChanged: boolean;
}

function applyScoreUpdate(state: HpClaimsQuestionSetPageStateProps, answers: HpAnswerMap): ApplyScoreUpdateResult {
  const { hasScoreChanged, scoringMap, totalScore } = recalculateScoringMap(
    state.questions,
    answers,
    state.scoringMap,
    state.displayState.questionsUI
  );
  if (hasScoreChanged) {
    setCustomerAnswer(answers, questionIds.scoreTotal, totalScore);
    return { state: applyChanges(state, { scoringMap }), hasScoreChanged };
  }

  return { state, hasScoreChanged };
}

export { questionSetReducer as questionSetReducerExtended };
